<template>
    <div class="page flex" style="padding-bottom: 0">
      <div class="container flex-grow px-2 lg:px-8">
        <Form class="form" ref="form" v-slot="{ errors, meta }">
        <div class="md:mt-28 md:flex">
          <div class="text-left md:w-3/5 md:mr-4 px-6 md:px-0">
            <div class="flex md:block justify-between">
                <Back :to="backTo" :toExternal="toExternal" />
              <div class="text-lg text-grey-2 md:text-grey-1 md:text-xl font-semibold -mt-1 md:font-bold md:mt-12 mb-6">
                {{ $t('loan_simulator.title') }}
              </div>
              <router-link to="dashboard">
                <img src="@/assets/svg/cross.svg" class="cursor-pointer -mt-1 block md:hidden"
                  alt="" />
              </router-link>
            </div>
            <div v-html="$t('loan_simulator.body')" class="text-grey-3 mb-4 md:mb-8 text-sm md:text-base">
    
            </div>
              <div class="mb-8">
                <div class="text-grey-2 text-base md:text-lg mb-4 font-semibold">{{ $t('loan_simulator.amount') }}</div>
                <GeneralInput 
                  rules="minProposal:2500000|maxProposal:100000000" 
                  :error="errors.amount" 
                  :locked="!interest" 
                  @input="onInput"
                  v-model:data="amount" 
                  type="dollar" 
                  name="amount" 
                  :label="$t('loan_simulator.amount_placeholder')" />
                <div class="ml-4 text-xs text-grey-3">{{ $t('loan_simulator.amount_helper') }}</div>
              </div>
              <div class="mb-8">
                <div class="text-grey-2 text-base md:text-lg mb-4 font-semibold">{{ $t('loan_simulator.term') }}</div>
                <GeneralInput 
                  :locked="!interest" 
                  @input="onInput" 
                  v-model:data="term" 
                  type="number"
                  name="term"
                  :label="$t('loan_simulator.term_placeholder')" 
                  rules="min:12|max:96" 
                  :error="errors.term"/>
                <div class="ml-4 text-xs text-grey-3">
                  <div v-if="!changedTerm">
                    {{ $t('loan_simulator.term_helper') }}
                  </div>
                  <div v-else>
                      <span v-if="years">
                        {{ $t('wording.year', years) }}
                      </span>
                      <span v-if="months">
                        <span v-if="years" class="ml-0.5">
                          {{ $t('wording.and') }}
                        </span>
                        {{ $t('wording.month', months) }}
                      </span>
                  </div>
                </div>
    
                <div class="mt-8">
                  <Slider @update="term = termSlider" :disabled="!interest" @change="onInput" v-model="termSlider" :min="12" :max="96" :step="1"
                    :tooltips="false" class="slider-loan-simulator" :lazy="false" />
                  <div class="flex justify-between mt-4">
                    <div class="text-xs font-medium text-grey-2">{{ $t('wording.month', 12) }}</div>
                    <div class="text-xs font-medium text-grey-2">{{ $t('wording.month', 96) }}</div>
                  </div>
                </div>
              </div>
            <div class="hidden md:block text-sm text-grey-3 p-4 border border-grey-5 rounded-3xl"
              v-html="$t('loan_simulator.info_box')">
            </div>
            <div class="hidden my-12 md:flex items-center bg-orange-sun-6 p-4 rounded-3xl"
              style="box-shadow: 4px 4px 10px 0px rgba(79, 89, 99, 0.10);">
              <div class="w-32 mr-6">
                <img :src="photo" alt="">
              </div>
              <div>
                <div class="font-semibold text-lg text-grey-1 mb-2">
                  {{ $t('loan_simulator.bizdev.title') +' '+ name }}
                </div>
                <div class="text-grey-2 font-medium">
                  {{ $t('loan_simulator.bizdev.body') }}
                  <span class="text-orange-1 hover:underline cursor-pointer font-medium">
                    <a :href="'mailto:' + (tag === 'business' ? 'borrowers-ca' : 'support-ca') + '@goparity.com'">
                      {{ $t('wording.email') }}
                    </a>
                  </span>
                    {{ $t('wording.or') }}
                  <span class="text-orange-1 hover:underline cursor-pointer font-medium">
                    <a
                    href="https://calendly.com/goparity-blake"
                    target="_blank"
                    >
                    {{ $t('loan_simulator.bizdev.book_call') }}
                    </a>
                  </span>.
                </div>
              </div>
            </div>
          </div>
          <div class="md:w-2/5 md:ml-4 px-6 text-left">
            <div class="md:bg-white rounded-3xl md:p-8 md:sticky top-8">
              <div class="bg-white p-6 md:p-0 rounded-3xl md:bg-transparent">
                <div class="flex">
                  <span class="text-grey-2 font-bold ">
                    {{ $t('loan_simulator.table.instalment') }}
                  </span>
                  <InfoTooltip :positionXAxisMobile="'-inset-x-32'" class="ml-2 mt-1" copy="loan_simulator.tooltips.initial" />
                </div>
                <InlineLoader class="mt-3" :loading="fetching" :displayText="dollarFormatting(firstInstalment)" textAppend="$"/>
              </div>
              <div class="rounded-2xl px-4 py-3 mb-6 mt-6 bg-grey-light md:bg-grey-ghost">
                <div class="flex justify-between text-sm font-medium text-grey-2 mb-1">
                  <div>{{ $t('loan_simulator.table.interest_rate') }}</div>
                  <div class="font-semibold">{{ interest ?? '' }}%</div>
                </div>
                <div class="text-grey-3 text-xs">{{ $t('loan_simulator.table.final_interest_rate') }}</div>
              </div>
              <div>
                <div class="text-grey-2 font-bold">{{ $t('loan_simulator.table.summary') }}</div>
                <div class="mt-4 flex text-sm text-grey-2 justify-between">
                  <div class="font-medium">{{ $t('loan_simulator.table.amount') }}</div>
                  <div class="font-semibold">${{ amount ?? dollarFormatting(0.0) }}</div>
                </div>
                <div class="mt-4 flex text-sm text-grey-2 justify-between">
                  <div class="font-medium flex">{{ $t('loan_simulator.table.total_interest') }}
                    <InfoTooltip :positionXAxisMobile="'-inset-x-24'" class="ml-2 mt-px" copy="loan_simulator.tooltips.interest" />
                  </div>
                  <div class="font-semibold">${{ dollarFormatting(totalInterest) }}</div>
                </div>
                <div class="mt-4 ">
                  <div class="flex cursor-pointer text-sm text-grey-2 justify-between w-full mb-4" @click="open = !open">
                    <div class="font-medium flex">{{ $t('loan_simulator.table.costs') }}
                      <InfoTooltip :positionXAxisMobile="'-inset-x-24'" class="ml-2 mt-px" copy="loan_simulator.tooltips.costs" @click="open = !open"/>
                    </div>
                    <div class="font-semibold flex">${{ dollarFormatting(totalCost) }} <img src="@/assets/svg/chev_down.svg"
                        alt="" class="-mr-1" :class="{ 'transform rotate-180': open }"></div>
                  </div>
                  <div :class="{ 'max-h-28': open }" class="max-h-0 overflow-hidden accordion">
                    <div class="flex text-xs justify-between w-full mb-2">
                      <div class="font-medium text-grey-3">{{ $t('loan_simulator.table.setup_fee') }}</div>
                      <div class="font-medium text-grey-2 flex">${{ dollarFormatting(setupCost) }}</div>
                    </div>
                    <div class="mb-2">
                      <div class="flex text-xs justify-between w-full">
                        <div class="font-medium text-grey-3">{{ $t('loan_simulator.table.ongoing_fee') }}</div>
                        <div class="font-medium text-grey-2 flex">${{ dollarFormatting(ongoingCost) }}</div>
                      </div>
                      <div class="text-grey-4 text-xs">{{ $t('loan_simulator.table.paid_monthly') }}</div>
                    </div>
                  </div>
                  <div class="border-t border-grey-6 w-full h-1 mb-4"></div>
                  <div class="mt-4 flex text-sm text-grey-2 justify-between">
                    <div class="font-medium flex">{{ $t('loan_simulator.table.total_cost') }}
                      <InfoTooltip class="ml-2 mt-px" copy="loan_simulator.tooltips.total_amount" />
                    </div>
                    <div class="font-semibold">${{ dollarFormatting(totalLoanCost) }}</div>
                  </div>
                  <div
                    class="bg-white md:bg-transparent py-5 rounded-2xl md:py-0 mt-12 mb-4 uppercase text-sm text-orange-1 flex font-bold text-center justify-center cursor-pointer hover:underline"
                    @click="(amount && term && meta.valid && !fetching) ? openPopup() : ''" :class="{'opacity-40 cursor-not-allowed hover:no-underline	': !term || !amount || !meta.valid || fetching}">
                    <div>{{ $t('loan_simulator.table.btn') }}</div>
                    <div class="ml-2 mt-px"><img src="@/assets/svg/orange_share.svg" alt=""></div>
                  </div>
                  <GpButton mobileWidth="w-full" desktopWidth="w-full" color="orange" class="mb-4 lg:mb-0" @click="next"
                    :disabled="!amount || !term || !meta.valid || fetching">
                    {{ $t('loan_simulator.table.primary-btn') }}
                  </GpButton>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="fixed z-50 popup-bg top-0 left-0 w-screen h-screen" :class="{ 'popup-bg--show': showPopup }"
            @click="closePopup">
          </div>
          <div style="border-radius: 3rem"
            class="fixed max-w-xl bg-white top-1/2 scale-0 transition-all left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 py-12 px-16"
            :class="{ 'scale-100': showPopup }">
            <div class="text-2xl text-grey-2 font-bold text-left">
              {{ $t('loan_simulator.email.title') }}
            </div>
            <div class="text-grey-3 mt-6">
              <GeneralInput v-model:data="email" type="email" name="email" bgColor="bg-grey-ghost" :label="$t('loan_simulator.email.placeholder')
                  " />
            </div>
            <div class="w-full mt-6">
              <GpButton :disabled="!email || !consent" desktopWidth="w-full" @click="sendSimulation">
                {{ $t('loan_simulator.email.btn') }}
              </GpButton>
            </div>
            <div class="mt-6">
              <GeneralInput v-model:data="consent" type="checkbox" name="consent" :label="$t('loan_simulator.email.consent')
                  " />
            </div>
          </div>
        </div>
        </Form>
      </div>
  
    </div>
  </template>
  
  <script>
  import Back from '@/components/general/Back.vue';
  import GeneralInput from '@/components/general/Input.vue';
  import InlineLoader from '@/components/general/InlineLoader.vue';
  import Slider from '@vueform/slider'
  import useGlobalHelpers from '@/mixins/useGlobalHelpers.js'
  import { useStore } from 'vuex'
  import { ref, onMounted, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import InfoTooltip from '@/components/general/InfoTooltip.vue';
  import GpButton from '@/components/general/GpButton.vue';
  import { debounce } from "lodash";
  import axios from 'axios'
  import { useRouter } from 'vue-router';
  import { Form } from 'vee-validate'
  
  export default {
    components: {
      Back,
      GeneralInput,
      Slider,
      Form,
      GpButton,
      InfoTooltip,
      InlineLoader
    },
    setup() {
      const amount = ref(null)
      const term = ref(12)
      const open = ref(false)
      const email = ref(null)
      const form = ref(null)
      const consent = ref(false)
      const termSlider = ref(12)
      const { dollarFormatting, dollarToNumberFormatting } = useGlobalHelpers()
      const i18n = useI18n({ useScope: 'global' })
      const store = useStore()
      const fetching = ref(false)
      const changedTerm = ref(false)
      const levels = [12, 24, 36, 48, 60, 72, 84, 96]
      const showPopup = ref(false)
      const router = useRouter()
      const toExternal = computed(() => {
      const entryPoint = sessionStorage.getItem('simulatorEntryPoint')
        
        if (entryPoint === 'dashboard') {
          return null
        }
        
        return {
          base: process.env.VUE_APP_PUBLIC_PAGES_URL,
          to: 'organizations/get-funded'
        }
      })

      const backTo = computed(() => {
        const entryPoint = sessionStorage.getItem('simulatorEntryPoint')
        return entryPoint === 'dashboard' ? '/dashboard' : null
      })
      const name = ref('')
      const tag = ref('business')
      const photo = ref('')
  
      const getTeamMember = () => {
        const params = {
          'filter[tag]': tag.value
        }
        axios.get(process.env.VUE_APP_SERVER_BASE_URL + 'api/v1/team', { params })
          .then((response) => {
            const res = response.data
            if (res.length > 0) {
              name.value = res[0].name.split(' ')[0]
              photo.value = res[0].photo.path
            }
          })
          .catch((error) => {
            console.error('Error fetching team data:', error)
          })
      }
  
      const riskValues = computed(() => {
        return store.state.general.riskValues
      })
  
      const simulation = computed(() => {
        return store.state.general.simulation
      })
  
      function openPopup() {
        showPopup.value = true
      }
  
      function closePopup() {
        showPopup.value = false
      }
  
      const onInput = debounce(async () => {
      if (term.value && term.value > 5) termSlider.value = term.value
      if (term.value !== 6 && !changedTerm.value) changedTerm.value = true
      const validation = await form.value.validate()
      if (validation.valid) {
        fetching.value = true
        await store.dispatch('setSimulation', {
          amount: dollarToNumberFormatting(amount.value),
          term: term.value,
          interest: interest.value
        })
        fetching.value = false
      }
    }, 300)
  
      const years = computed(() => {
        return Math.floor(term.value/12)
      })
  
      const months = computed(() => {
        return term.value - years.value * 12
      })
  
      const closestLevelIndex = computed(() => {
        const userInput = typeof term.value === 'string' ? parseInt(term.value) : term.value;
  
        // Check if the user input matches any level exactly
        const exactMatch = levels.find(level => level === userInput);
  
        if (exactMatch !== undefined) {
          return levels.indexOf(exactMatch); // Return the exact match if found
        }
  
        // Find the closest level rounded upwards
        const closest = levels.find(level => level >= userInput);
  
        return closest !== undefined ? levels.indexOf(closest) : levels.indexOf(levels[levels.length - 1]);
      });
  
      const interest = computed(() => {
        return riskValues.value ? riskValues.value[closestLevelIndex.value] : 0
      })
  
      const totalInterest = computed(() => {
        return simulation.value ? simulation.value.total_interest : 0
      })
  
      const setupCost = computed(() => {
        return simulation.value ? simulation.value.assembly_comission : 0
      })
  
      const ongoingCost = computed(() => {
        return simulation.value ? simulation.value.maintanance_total : 0
      })
  
      const totalCost = computed(() => {
        return setupCost.value + ongoingCost.value
      })
  
      const totalLoanCost = computed(() => {
        return totalCost.value + totalInterest.value + dollarToNumberFormatting(amount.value)
      })
  
      const firstInstalment = computed(() => {
        return simulation.value ? simulation.value.monthly_instalment : 0
      })
  
      function next() {
        sessionStorage.removeItem('simulatorEntryPoint')
        store.dispatch('setFormData', { 
          "amount": dollarToNumberFormatting(amount.value), 
          "term_months": term.value,
          "interest_rate": interest.value,
          "fee_upfront": setupCost.value,
          "fee_ongoing": ongoingCost.value
        })
        router.push('proposal')
      }
  
      const sendSimulation = () => {
        store.dispatch('setLoading', true)
        axios.get(
          process.env.VUE_APP_SERVER_BASE_URL + 'api/v1/simulation/send',
          {
            params: { 
                amount: dollarToNumberFormatting(amount.value), 
                term: term.value, 
                interest: interest.value, 
                email: email.value, 
                language: i18n.locale.value 
              }
          }
        ).then(({ data }) => {
          store.dispatch('setLoading', false)
          showPopup.value = false
          const popup = {
            title: i18n.t('loan_simulator.email.popup.success.title'),
            content: i18n.t('loan_simulator.email.popup.success.content'),
            align: 'center',
            side: false,
            iconBg: 'bg-green-5',
            icon: '/img/done_step.svg',
            primaryButton: i18n.t(
                'loan_simulator.email.popup.success.btn'
            )
          }
          store.dispatch('setPopup', popup)
        }).catch((e) => {
          store.dispatch('setLoading', false)
          showPopup.value = false
        })
      }
  
      onMounted(() => {
        store.dispatch('setRiskValues')
        getTeamMember()
      })
  
      return {
        amount,
        term,
        next,
        open,
        dollarFormatting,
        sendSimulation,
        totalLoanCost,
        riskValues,
        firstInstalment,
        simulation,
        closestLevelIndex,
        interest,
        totalInterest,
        setupCost,
        openPopup,
        ongoingCost,
        changedTerm,
        form,
        totalCost,
        years,
        months,
        email,
        consent,
        fetching,
        termSlider,
        showPopup,
        closePopup,
        onInput,
        toExternal,
        InlineLoader,
        getTeamMember,
        name,
        photo,
        tag,
        backTo
      }
  
    }
  }
  </script>
  
  <style>
  .slider-loan-simulator {
    --slider-connect-bg: #B3DABB;
    --slider-bg: #EAEAEB;
    --slider-handle-ring-color: #ffffff;
    --slider-handle-bg: #B3DABB;
    --slider-handle-ring-width: 6px;
    --slider-handle-radius: 1000px;
    --slider-tooltip-bg: #B3DABB;
    --slider-tooltip-py: 0.25rem;
    --slider-tooltip-px: 0.25rem;
  }
  
  .accordion {
    transition: max-height 0.5s ease;
  }
  
  .popup-bg {
    background-color: rgba(79, 89, 99, 0);
    transition: all 0.2s ease-in-out;
    pointer-events: none;
    backdrop-filter: blur(0px);
  }
  
  .popup-bg--show {
    pointer-events: all;
    backdrop-filter: blur(2px);
    background-color: rgba(79, 89, 99, 0.8);
  }
  </style>
  